// NEXT_PUBLIC_APP_URL must be set in production to the canonical app origin
// (e.g. https://clinic.example.com). It is used in invite email redirect URLs
// to prevent host-header injection attacks. Without it the fallback is
// http://localhost:3000, which is only safe for local development.
const REQUIRED_ENV_VARS = [
  'NEXT_PUBLIC_SUPABASE_URL',
  'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY',
  'SUPABASE_SECRET_KEY',
] as const

export type RequiredEnvVar = (typeof REQUIRED_ENV_VARS)[number]

function sanitizeEnvValue(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim().replace(/[\r\n]/g, '')
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeSupabaseUrl(value: string): string {
  // Defensive cleanup for accidentally pasted encoded CR/LF suffixes.
  const cleaned = value.replace(/(%0A|%0D)+$/ig, '')

  try {
    return new URL(cleaned).origin
  } catch {
    return cleaned
  }
}

// Direct property access so Next.js can statically replace NEXT_PUBLIC_*
// values at build time. Dynamic process.env[name] would be stripped.
function resolveEnv(name: RequiredEnvVar): string | undefined {
  switch (name) {
    case 'NEXT_PUBLIC_SUPABASE_URL':
      return process.env.NEXT_PUBLIC_SUPABASE_URL
    case 'NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY':
      return process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY
    case 'SUPABASE_SECRET_KEY':
      return process.env.SUPABASE_SECRET_KEY
  }
}

export function getRequiredEnv(name: RequiredEnvVar): string {
  const value = sanitizeEnvValue(resolveEnv(name))
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`)
  }
  return value
}

export function getSupabasePublicEnv() {
  const url = normalizeSupabaseUrl(getRequiredEnv('NEXT_PUBLIC_SUPABASE_URL'))
  const publishableKey = getRequiredEnv('NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY')
  return { url, publishableKey }
}

export function getSupabaseSecretEnv() {
  return {
    ...getSupabasePublicEnv(),
    secretKey: getRequiredEnv('SUPABASE_SECRET_KEY'),
  }
}

/**
 * Canonical app origin for auth email redirects (invites, password reset).
 * Required in production to prevent host-header injection on invite/reset links.
 */
export function getAppBaseUrl(): string {
  const baseUrl = process.env.NEXT_PUBLIC_APP_URL?.trim()

  if (process.env.NODE_ENV === 'production') {
    if (!baseUrl) {
      throw new Error('NEXT_PUBLIC_APP_URL environment variable is required in production')
    }
    return baseUrl
  }

  return baseUrl || 'http://localhost:3000'
}

/** Optional boolean env var. True only when value is '1' or 'true' (case-insensitive). */
export function getOptionalBoolEnv(name: string): boolean {
  const v = process.env[name]
  if (v === undefined || v === '') return false
  return v === '1' || v.toLowerCase() === 'true'
}
